/**
 * The search screen's data layer: one call per question, and the four things that can
 * come back.
 *
 * ── IT DECIDES NOTHING ABOUT THE QUERY ──────────────────────────────────────
 * `q` is passed to `postSearch` and is otherwise untouched. No length check, no
 * question-mark check, no "this looks conversational" — that is the server's decision
 * (audit finding 01), and a client that makes it reintroduces the split the finding
 * removed, in a shipped bundle where nobody can fix it centrally.
 *
 * ── THE FOUR OUTCOMES, AND WHY THERE IS NO FIFTH ────────────────────────────
 *   loading            the request is in flight — S3's drawn skeleton, query in field;
 *                      or, when a chip changed, the answer before it stays on screen
 *   ready              the Worker answered; `result.route` says which screen
 *   designed_failure   an `ApiErrorBody`: one of the nine codes, one of the eleven
 *                      states, with the server's own `message_bn`
 *   unreachable        the answer never arrived
 *
 * There is deliberately no generic-failure outcome, because there is no generic screen
 * (`packages/contracts/src/errors.ts`, docs/10 § WP-K). `UnmappedApiError` — a failure
 * the server described with no designed state — is folded into `unreachable` AND
 * written to the console with its status, so it shows up as a gap in what the backend
 * reports rather than being absorbed into a shrug. What the user is told is the one
 * true thing either way: no answer arrived, here is how to try again.
 *
 * REPORTED: that fold is a judgement call. `E_OFFLINE`'s frame says ইন্টারনেট নেই, and
 * a 500 from the Worker is not the internet being down. No state was drawn for "the
 * server answered something nobody designed", and inventing one — or a sentence for one
 * — is what docs/01 forbids, so the closest drawn state is used and the difference is
 * recorded here and in the console rather than papered over.
 *
 * ── WHY A CONTROLLER AND NOT AN EFFECT PER FILTER (W2-08) ───────────────────
 * B3's chips used to change their own highlight and nothing else: the list under a
 * selected `যাচাইকৃত` was the unfiltered list. A chip is now a question to the Worker —
 * `filter` travels in the body — and the question is asked WHEN THE CHIP IS TAPPED, by the
 * handler, not by an effect noticing a state change a render later. That is what a chip
 * is (an action), and it is what makes the tap → POST chain provable in this repo's
 * DOM-free tests: `__tests__/interaction.test.tsx` taps the chip element the screen draws
 * and reads the body the controller sent. The first question for a query is still asked
 * by the one effect in `useSearch`, because arriving at `/search?q=` is not a tap.
 */
import { useEffect, useMemo, useSyncExternalStore } from 'react';
import type { ApiErrorBody, SearchFilter, SearchRequest } from '@oprappo/contracts';
import { ApiError, NetworkError, currentAccessToken } from '../../api/client.js';
import { postSearch, type SearchResult } from './client.js';
import { rememberRecent } from './recent-store.js';

export type SearchState =
  | { status: 'idle' }
  /** `previous` is the answer on screen while a chip's question is in flight. */
  | { status: 'loading'; previous?: SearchResult | undefined }
  | { status: 'ready'; result: SearchResult }
  | { status: 'designed_failure'; error: ApiErrorBody }
  | { status: 'unreachable' };

export interface SearchSnapshot {
  /** The chip that is lit — the filter the last question was asked under. */
  filter: SearchFilter;
  state: SearchState;
}

export interface SearchControllerDeps {
  post: (body: SearchRequest, init: { signal: AbortSignal }) => Promise<SearchResult>;
  /** Told once per query arrived at — never per chip. See `useSearch` for who listens. */
  remember?: ((q: string) => void) | undefined;
}

export interface SearchController {
  readonly q: string;
  snapshot: () => SearchSnapshot;
  subscribe: (listener: () => void) => () => void;
  /** Ask the first question for this query. */
  start: () => void;
  /** A chip was tapped: light it and ask again, under it. Tapping the lit chip asks nothing. */
  choose: (filter: SearchFilter) => void;
  /** The one way out of `unreachable`, which is the way the offline frame draws. */
  retry: () => void;
  /** The screen left, or the query changed. Whatever is in flight is abandoned. */
  stop: () => void;
}

/**
 * The body, and the only place it is composed. `সব` sends no filter at all, so the request
 * for an unfiltered search is exactly what it was before chips could filter.
 */
export function searchRequest(q: string, filter: SearchFilter): SearchRequest {
  return filter === 'all' ? { q, mode: 'auto' } : { q, mode: 'auto', filter };
}

export function createSearchController(q: string, deps: SearchControllerDeps): SearchController {
  let snap: SearchSnapshot = { filter: 'all', state: q === '' ? { status: 'idle' } : { status: 'loading' } };
  const listeners = new Set<() => void>();
  let inflight: AbortController | null = null;

  const set = (next: SearchSnapshot): void => {
    snap = next;
    for (const listener of listeners) listener();
  };

  const onScreen = (): SearchResult | undefined =>
    snap.state.status === 'ready'
      ? snap.state.result
      : snap.state.status === 'loading'
        ? snap.state.previous
        : undefined;

  const ask = (): void => {
    if (q === '') {
      set({ ...snap, state: { status: 'idle' } });
      return;
    }
    inflight?.abort();
    const request = new AbortController();
    inflight = request;
    const filter = snap.filter;
    set({ filter, state: { status: 'loading', previous: onScreen() } });

    deps
      .post(searchRequest(q, filter), { signal: request.signal })
      .then((result) => {
        if (inflight !== request) return;
        inflight = null;
        set({ filter, state: { status: 'ready', result } });
      })
      .catch((err: unknown) => {
        if (inflight !== request) return;
        inflight = null;
        // The caller's own abort — a new query, or the screen leaving. Not a failure.
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError) {
          set({ filter, state: { status: 'designed_failure', error: err.body } });
          return;
        }
        if (!(err instanceof NetworkError)) {
          // A failure the API described in a way no screen was drawn for. Said out
          // loud: this is backend information that is missing, not a state.
          console.error('POST /v1/search failed with no designed state', err);
        }
        set({ filter, state: { status: 'unreachable' } });
      });
  };

  return {
    q,
    snapshot: () => snap,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    start: () => {
      if (q !== '') deps.remember?.(q);
      ask();
    },
    choose: (filter) => {
      if (filter === snap.filter) return;
      snap = { ...snap, filter };
      ask();
    },
    retry: () => ask(),
    stop: () => {
      inflight?.abort();
      inflight = null;
    },
  };
}

export interface UseSearch {
  controller: SearchController;
  filter: SearchFilter;
  state: SearchState;
  /** The one way out of `unreachable`, which is the way the offline frame draws. */
  retry: () => void;
}

/**
 * A signed-out person's search goes on this device's recents; a signed-in person's is
 * already recorded by the Worker, against them, and is read back from there.
 */
function rememberIfSignedOut(q: string): void {
  if (currentAccessToken() === null) rememberRecent(q);
}

/**
 * One controller per query. An empty query never calls: B2 is the screen for it, and it
 * is a place you start rather than a thing you ask the server about.
 */
export function useSearch(q: string): UseSearch {
  const controller = useMemo(
    () => createSearchController(q, { post: postSearch, remember: rememberIfSignedOut }),
    [q],
  );
  useEffect(() => {
    controller.start();
    return () => controller.stop();
  }, [controller]);
  const snap = useSyncExternalStore(controller.subscribe, controller.snapshot, controller.snapshot);
  return { controller, filter: snap.filter, state: snap.state, retry: controller.retry };
}
